import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getInitials } from "@/lib/utils.ts";
import { format } from "date-fns";
import { ResearchBadge } from "@/components/users/research-badge.tsx";
import {
  buildRedactedPseudoId,
  inferRedactedFieldsFromNulls,
  isFieldRedacted,
} from "@/lib/redacted-display";

interface MentorshipNameProps {
  mentorName: string;
  menteeName: string | null;
  lastMessage?: string;
  lastMessageDate?: string;
  mentorProfilePictureUrl: string;
  menteeProfilePictureUrl: string | null;
  isMenteeResearchParticipant?: boolean;
  notificationsCount?: number | null;
  onNotificationsClick?: () => void;
  menteeUserId?: string;
  menteeRedactedFields?: string[];
}

export function MentorshipName(props: MentorshipNameProps) {
  const redactedFields =
    props.menteeRedactedFields ??
    inferRedactedFieldsFromNulls({
      name: props.menteeName,
      profile_picture_url: props.menteeProfilePictureUrl,
    });
  const menteeNameRedacted = isFieldRedacted("name", redactedFields);
  const menteePhotoRedacted = isFieldRedacted(
    "profile_picture_url",
    redactedFields,
  );

  const { url: mentorProfilePictureUrl } = useSignedMedia(
    props.mentorProfilePictureUrl,
  );
  const { url: menteeProfilePictureUrl } = useSignedMedia(
    menteePhotoRedacted ? "" : (props.menteeProfilePictureUrl ?? ""),
  );

  const menteeDisplayName = menteeNameRedacted
    ? buildRedactedPseudoId(props.menteeUserId)
    : (props.menteeName ?? "");

  return (
    <div className="flex items-center">
      <div className="relative flex">
        <Avatar className="">
          {!menteePhotoRedacted && (
            <AvatarImage src={menteeProfilePictureUrl} />
          )}
          <AvatarFallback>
            {menteeNameRedacted ? "?" : getInitials(props.menteeName ?? "")}
          </AvatarFallback>
        </Avatar>
        <Avatar className="-left-4">
          <AvatarImage src={mentorProfilePictureUrl} />
          <AvatarFallback>{getInitials(props.mentorName)}</AvatarFallback>
        </Avatar>
        {!!props.notificationsCount && (
          <button
            onClick={props.onNotificationsClick}
            className="absolute right-2 top-0 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 text-xs text-white"
          >
            {props.notificationsCount > 99 ? "99+" : props.notificationsCount}
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <div className="inline-flex items-center justify-between text-base font-bold leading-tight text-black">
          <div className="flex items-center gap-1">
            {menteeDisplayName} & {props.mentorName}
            <ResearchBadge
              isResearchParticipant={props.isMenteeResearchParticipant}
            />
          </div>
          {props.lastMessageDate && (
            <span className="ml-2 text-xs font-normal leading-none text-zinc-400">
              {format(new Date(props.lastMessageDate), "MMM d, yyyy h:mm a")}
            </span>
          )}
        </div>
        {props.lastMessage && (
          <span className="mt-1 max-w-md overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal leading-none text-zinc-600">
            {props.lastMessage}
          </span>
        )}
      </div>
    </div>
  );
}
